import React, { useEffect, useState } from 'react';
import {
    LineChart, Line, ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import Header from '../../components/header';
import Layout from '../../components/layout';
import { Dropdown, Menu } from '../../components/Dropdown';
import { downloadPDF, downloadExcel } from '../../_common/functions';
import { useDashboardApi } from '../../store/api/hooks/dashboardApiHook';

// ─── Types ────────────────────────────────────────────────────────────────────

type TimePeriod = '1W' | '3W' | '30D' | '3Mo' | '6Mo' | '12Mo';

const TIME_PERIODS: TimePeriod[] = ['1W', '3W', '30D', '3Mo', '6Mo', '12Mo'];

const PERIOD_LABELS: Record<TimePeriod, string> = {
    '1W': 'Last 1 week',
    '3W': 'Last 3 weeks',
    '30D': 'Last 30 days',
    '3Mo': 'Last 3 months',
    '6Mo': 'Last 6 months',
    '12Mo': 'Last 12 months',
};

const PERIOD_DAYS: Record<TimePeriod, number> = {
    '1W': 7, '3W': 21, '30D': 30, '3Mo': 90, '6Mo': 180, '12Mo': 365,
};

function getGranularity(period: TimePeriod): string {
    if (period === '1W') return 'Day';
    if (period === '3W') return 'Week';
    if (period === '30D') return 'Week';
    if (period === '3Mo') return 'Month';
    return 'Month';
}

// ─── Chart 1 / 3 data helpers ─────────────────────────────────────────────────

interface WRFacilityRow {
    bucketLabel: string;
    facilityID: number;
    facilityName: string;
    wrCount: number;
    sysAvg: number;
}

interface TaskFacilityRow {
    bucketLabel: string;
    facilityID: number;
    facilityName: string;
    taskCount: number;
    sysAvg: number;
}

interface PivotedFacility { facilityID: number; facilityName: string; }

function pivotWRFacilityRows(rows: WRFacilityRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
} {
    const bucketMap = new Map<string, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    for (const row of rows) {
        if (!bucketMap.has(row.bucketLabel)) {
            bucketMap.set(row.bucketLabel, { label: row.bucketLabel, sysAvg: row.sysAvg });
        }
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}`] = row.wrCount;
        facilityMap.set(row.facilityID, row.facilityName);
    }
    return {
        chartData: Array.from(bucketMap.values()),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
    };
}

function pivotTaskFacilityRows(rows: TaskFacilityRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
} {
    const bucketMap = new Map<string, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    for (const row of rows) {
        if (!bucketMap.has(row.bucketLabel)) {
            bucketMap.set(row.bucketLabel, { label: row.bucketLabel, sysAvg: row.sysAvg });
        }
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}`] = row.taskCount;
        facilityMap.set(row.facilityID, row.facilityName);
    }
    return {
        chartData: Array.from(bucketMap.values()),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
    };
}

// ─── Chart 2 / 4 data types ───────────────────────────────────────────────────

interface WROverTimeRow {
    bucketLabel: string;
    userWRCount: number;
    peerAvg: number;
    roleName: string;
    hasPeerData: boolean;
}

interface TaskOverTimeRow {
    bucketLabel: string;
    userTaskCount: number;
    peerAvg: number;
    roleName: string;
    hasPeerData: boolean;
}

// ─── Chart 5 / 6 data types ───────────────────────────────────────────────────

interface RespByFarmRow {
    binOrder: number;
    binLabel: string;
    facilityID: number;
    facilityName: string;
    taskCount: number;
    sysAvgHours: number;
}

interface RespIndividualRow {
    binOrder: number;
    binLabel: string;
    userCount: number;
    peerAvgHours: number;
    roleName: string;
    hasPeerData: boolean;
}

function hoursToBucket(hours: number): string {
    if (hours < 1) return '<1 hr';
    if (hours < 2) return '<2 hrs';
    if (hours < 4) return '<4 hrs';
    if (hours < 8) return '<8 hrs';
    if (hours < 24) return '<24 hrs';
    if (hours < 48) return '<2 days';
    return '2+ days';
}

function pivotRespByFarmRows(rows: RespByFarmRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
    sysAvgHours: number;
} {
    const binMap = new Map<number, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    let sysAvgHours = 0;
    for (const row of rows) {
        if (!binMap.has(row.binOrder)) {
            binMap.set(row.binOrder, { label: row.binLabel });
        }
        binMap.get(row.binOrder)![`f_${row.facilityID}`] = row.taskCount;
        facilityMap.set(row.facilityID, row.facilityName);
        sysAvgHours = row.sysAvgHours;
    }
    return {
        chartData: Array.from(binMap.entries()).sort((a, b) => a[0] - b[0]).map(([, v]) => v),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
        sysAvgHours,
    };
}

// ─── Chart 7 / 8 data types ───────────────────────────────────────────────────

interface WalkByFarmRow {
    bucketLabel: string;
    facilityID: number;
    facilityName: string;
    completedCount: number;
    incompleteCLCount: number;
    sysAvg: number;
}

interface WalkOverTimeRow {
    bucketLabel: string;
    completedCount: number;
    incompleteCLCount: number;
    peerAvg: number;
    roleName: string;
    hasPeerData: boolean;
}

function pivotWalkByFarmRows(rows: WalkByFarmRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
} {
    const bucketMap = new Map<string, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    for (const row of rows) {
        if (!bucketMap.has(row.bucketLabel)) {
            bucketMap.set(row.bucketLabel, { label: row.bucketLabel, sysAvg: row.sysAvg });
        }
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_c`] = row.completedCount;
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_i`] = row.incompleteCLCount;
        facilityMap.set(row.facilityID, row.facilityName);
    }
    return {
        chartData: Array.from(bucketMap.values()),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
    };
}

const formatBucketTick = (v: string) => v.split('|')[0] || v;

const BucketXTick = ({ x, y, payload }: any) => {
    const [main, sub] = String(payload?.value ?? '').split('|');
    return (
        <g transform={`translate(${x},${y})`}>
            <text textAnchor='middle'>
                <tspan x={0} dy='0.8em' fontSize={11} fill='#6b7280'>{main}</tspan>
                {sub && <tspan x={0} dy='1.3em' fontSize={10} fill='#9ca3af'>{sub}</tspan>}
            </text>
        </g>
    );
};

// ─── Chart 9 / 10 data types ─────────────────────────────────────────────────

interface PMByFarmRow {
    bucketLabel: string;
    facilityID: number;
    facilityName: string;
    onTimeCount: number;
    lateCount: number;
    avgOnTime: number;
    avgLate: number;
}

interface PMOverTimeRow {
    bucketLabel: string;
    onTimeCount: number;
    lateCount: number;
    peerAvgOnTime: number;
    peerAvgLate: number;
    roleName: string;
    hasPeerData: boolean;
}

function pivotPMByFarmRows(rows: PMByFarmRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
    avgOnTime: number;
    avgLate: number;
} {
    const bucketMap = new Map<string, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    let avgOnTime = 0;
    let avgLate = 0;
    for (const row of rows) {
        if (!bucketMap.has(row.bucketLabel)) {
            bucketMap.set(row.bucketLabel, { label: row.bucketLabel });
        }
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_on`] = row.onTimeCount;
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_late`] = row.lateCount;
        facilityMap.set(row.facilityID, row.facilityName);
        avgOnTime = row.avgOnTime;
        avgLate = row.avgLate;
    }
    return {
        chartData: Array.from(bucketMap.values()),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
        avgOnTime,
        avgLate,
    };
}

// ─── Chart 11 / 12 data types ────────────────────────────────────────────────

interface ReactiveByFarmRow {
    bucketLabel: string;
    facilityID: number;
    facilityName: string;
    taskCount: number;
    hoursLogged: number;
    avgTasks: number;
    avgHrs: number;
}

interface ReactiveOverTimeRow {
    bucketLabel: string;
    taskCount: number;
    hoursLogged: number;
    peerAvgTasks: number;
    peerAvgHrs: number;
    roleName: string;
    hasPeerData: boolean;
}

function pivotReactiveByFarmRows(rows: ReactiveByFarmRow[]): {
    chartData: Record<string, any>[];
    facilities: PivotedFacility[];
    avgTasks: number;
    avgHrs: number;
} {
    const bucketMap = new Map<string, Record<string, any>>();
    const facilityMap = new Map<number, string>();
    let avgTasks = 0;
    let avgHrs = 0;
    for (const row of rows) {
        if (!bucketMap.has(row.bucketLabel)) {
            bucketMap.set(row.bucketLabel, { label: row.bucketLabel });
        }
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_t`] = row.taskCount;
        bucketMap.get(row.bucketLabel)![`f_${row.facilityID}_h`] = row.hoursLogged;
        facilityMap.set(row.facilityID, row.facilityName);
        avgTasks = row.avgTasks;
        avgHrs = row.avgHrs;
    }
    return {
        chartData: Array.from(bucketMap.values()),
        facilities: Array.from(facilityMap.entries()).map(([facilityID, facilityName]) => ({ facilityID, facilityName })),
        avgTasks,
        avgHrs,
    };
}

// ─── Shared UI helpers ────────────────────────────────────────────────────────

const FACILITY_COLORS = ['#16a34a', '#2563eb', '#d97706', '#7c3aed', '#0891b2', '#be185d'];
const FACILITY_LATE_COLORS = ['#86efac', '#93c5fd', '#fde68a', '#c4b5fd', '#a5f3fc', '#fbcfe8'];

const CARD_SHADOW = { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

const TOOLTIP_STYLE = {
    contentStyle: { fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb' },
    labelStyle: { color: '#111827', fontWeight: 600 },
};

const AXIS_PROPS = {
    tick: { fontSize: 11, fill: '#6b7280' },
    axisLine: { stroke: '#e5e7eb' },
    tickLine: false as const,
};

const LineSwatch = ({ color, dashed = false }: { color: string; dashed?: boolean }) => (
    <svg width={22} height={6} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <line x1={0} y1={3} x2={22} y2={3} stroke={color} strokeWidth={2} strokeDasharray={dashed ? '5 4' : undefined} />
    </svg>
);

const SquareSwatch = ({ color }: { color: string }) => (
    <span className='inline-block w-3 h-3 rounded-sm flex-shrink-0' style={{ background: color }} />
);

const LoadingState = () => (
    <div className='flex justify-center items-center h-[280px] text-gray-400 text-[13px]'>Loading…</div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const MyDashboardV2: React.FC = () => {
    const {
        getMyDashboardWRByFacilityOverTime,
        exportMyDashboardWRByFacilityOTToPDF,
        exportMyDashboardWRByFacilityOTToXLS,
        getMyDashboardWROverTime,
        exportMyDashboardWROverTimeToPDF,
        exportMyDashboardWROverTimeToXLS,
        getMyDashboardTasksByFacilityOverTime,
        exportMyDashboardTasksByFacilityOTToPDF,
        exportMyDashboardTasksByFacilityOTToXLS,
        getMyDashboardTasksOverTime,
        exportMyDashboardTasksOverTimeToPDF,
        exportMyDashboardTasksOverTimeToXLS,
        getMyDashboardResponsivenessByFarm,
        exportMyDashboardRespByFarmToPDF,
        exportMyDashboardRespByFarmToXLS,
        getMyDashboardResponsivenessIndividual,
        exportMyDashboardRespIndToPDF,
        exportMyDashboardRespIndToXLS,
        getMyDashboardWRCompletenessByFarm,
        exportMyDashboardWRCompletenessByFarmToPDF,
        exportMyDashboardWRCompletenessByFarmToXLS,
        getMyDashboardWRCompletenessByMe,
        exportMyDashboardWRCompletenessByMeToPDF,
        exportMyDashboardWRCompletenessByMeToXLS,
        getMyDashboardWalkthroughsByFarm,
        exportMyDashboardWalkByFarmToPDF,
        exportMyDashboardWalkByFarmToXLS,
        getMyDashboardWalkthroughsOverTime,
        exportMyDashboardWalkOverTimeToPDF,
        exportMyDashboardWalkOverTimeToXLS,
        getMyDashboardPMByFarm,
        exportMyDashboardPMByFarmToPDF,
        exportMyDashboardPMByFarmToXLS,
        getMyDashboardPMOverTime,
        exportMyDashboardPMOverTimeToPDF,
        exportMyDashboardPMOverTimeToXLS,
        getMyDashboardReactiveByFarm,
        exportMyDashboardReactiveByFarmToPDF,
        exportMyDashboardReactiveByFarmToXLS,
        getMyDashboardReactiveOverTime,
        exportMyDashboardReactiveOverTimeToPDF,
        exportMyDashboardReactiveOverTimeToXLS,
    } = useDashboardApi();

    const [timePeriod, setTimePeriod] = useState<TimePeriod>('30D');

    // Chart 1 state
    const [wrFacilityChartData, setWrFacilityChartData] = useState<Record<string, any>[]>([]);
    const [wrFacilities, setWrFacilities] = useState<PivotedFacility[]>([]);
    const [loadingChart1, setLoadingChart1] = useState(false);

    // Chart 2 state
    const [wrOverTime, setWrOverTime] = useState<WROverTimeRow[]>([]);
    const [loadingChart2, setLoadingChart2] = useState(false);

    // Chart 3 state
    const [taskFacilityChartData, setTaskFacilityChartData] = useState<Record<string, any>[]>([]);
    const [taskFacilities, setTaskFacilities] = useState<PivotedFacility[]>([]);
    const [loadingChart3, setLoadingChart3] = useState(false);

    // Chart 4 state
    const [taskOverTime, setTaskOverTime] = useState<TaskOverTimeRow[]>([]);
    const [loadingChart4, setLoadingChart4] = useState(false);

    // Chart 5 state
    const [respByFarmChartData, setRespByFarmChartData] = useState<Record<string, any>[]>([]);
    const [respFarmFacilities, setRespFarmFacilities] = useState<PivotedFacility[]>([]);
    const [respFarmSysAvgHours, setRespFarmSysAvgHours] = useState<number>(0);
    const [loadingChart5, setLoadingChart5] = useState(false);

    // Chart 6 state
    const [respIndividual, setRespIndividual] = useState<RespIndividualRow[]>([]);
    const [loadingChart6, setLoadingChart6] = useState(false);

    // Chart 9 state
    const [pmByFarmChartData, setPmByFarmChartData] = useState<Record<string, any>[]>([]);
    const [pmFarmFacilities, setPmFarmFacilities] = useState<PivotedFacility[]>([]);
    const [pmFarmAvgOnTime, setPmFarmAvgOnTime] = useState<number>(0);
    const [pmFarmAvgLate, setPmFarmAvgLate] = useState<number>(0);
    const [loadingChart9, setLoadingChart9] = useState(false);

    // Chart 10 state
    const [pmOverTime, setPmOverTime] = useState<PMOverTimeRow[]>([]);
    const [loadingChart10, setLoadingChart10] = useState(false);

    // Chart 7 state
    const [walkByFarmChartData, setWalkByFarmChartData] = useState<Record<string, any>[]>([]);
    const [walkFarmFacilities, setWalkFarmFacilities] = useState<PivotedFacility[]>([]);
    const [loadingChart7, setLoadingChart7] = useState(false);

    // Chart 8 state
    const [walkOverTime, setWalkOverTime] = useState<WalkOverTimeRow[]>([]);
    const [loadingChart8, setLoadingChart8] = useState(false);

    // Chart 11 state
    const [reactiveByFarmChartData, setReactiveByFarmChartData] = useState<Record<string, any>[]>([]);
    const [reactiveFarmFacilities, setReactiveFarmFacilities] = useState<PivotedFacility[]>([]);
    const [reactiveFarmAvgTasks, setReactiveFarmAvgTasks] = useState<number>(0);
    const [reactiveFarmAvgHrs, setReactiveFarmAvgHrs] = useState<number>(0);
    const [loadingChart11, setLoadingChart11] = useState(false);

    // Chart 12 state
    const [reactiveOverTime, setReactiveOverTime] = useState<ReactiveOverTimeRow[]>([]);
    const [loadingChart12, setLoadingChart12] = useState(false);

    // WR Completeness by Farm state
    const [wrCompleteness, setWrCompleteness] = useState<any[]>([]);
    const [loadingCompleteness, setLoadingCompleteness] = useState(false);

    // WR Completeness by Me state
    const [wrCompletenessByMe, setWrCompletenessByMe] = useState<any[]>([]);
    const [loadingCompletenessByMe, setLoadingCompletenessByMe] = useState(false);

    const [exportingChart, setExportingChart] = useState<string | null>(null);

    const days = PERIOD_DAYS[timePeriod];
    const granularity = getGranularity(timePeriod);

    // ── Fetchers ────────────────────────────────────────────────────────────

    const fetchChart1 = () => {
        setLoadingChart1(true);
        getMyDashboardWRByFacilityOverTime(
            { days, granularity },
            (res: any) => {
                const { chartData, facilities } = pivotWRFacilityRows(res?.data ?? []);
                setWrFacilityChartData(chartData);
                setWrFacilities(facilities);
                setLoadingChart1(false);
            },
            () => setLoadingChart1(false),
        );
    };

    const fetchChart2 = () => {
        setLoadingChart2(true);
        getMyDashboardWROverTime(
            { days, granularity },
            (res: any) => {
                const rows: WROverTimeRow[] = res?.data ?? [];
                setWrOverTime(rows);
                setLoadingChart2(false);
            },
            () => setLoadingChart2(false),
        );
    };

    const fetchChart3 = () => {
        setLoadingChart3(true);
        getMyDashboardTasksByFacilityOverTime(
            { days, granularity },
            (res: any) => {
                const { chartData, facilities } = pivotTaskFacilityRows(res?.data ?? []);
                setTaskFacilityChartData(chartData);
                setTaskFacilities(facilities);
                setLoadingChart3(false);
            },
            () => setLoadingChart3(false),
        );
    };

    const fetchChart4 = () => {
        setLoadingChart4(true);
        getMyDashboardTasksOverTime(
            { days, granularity },
            (res: any) => {
                const rows: TaskOverTimeRow[] = res?.data ?? [];
                setTaskOverTime(rows);
                setLoadingChart4(false);
            },
            () => setLoadingChart4(false),
        );
    };

    const fetchChart5 = () => {
        setLoadingChart5(true);
        getMyDashboardResponsivenessByFarm(
            { days },
            (res: any) => {
                const { chartData, facilities, sysAvgHours } = pivotRespByFarmRows(res?.data ?? []);
                setRespByFarmChartData(chartData);
                setRespFarmFacilities(facilities);
                setRespFarmSysAvgHours(sysAvgHours);
                setLoadingChart5(false);
            },
            () => setLoadingChart5(false),
        );
    };

    const fetchChart6 = () => {
        setLoadingChart6(true);
        getMyDashboardResponsivenessIndividual(
            { days },
            (res: any) => {
                setRespIndividual(res?.data ?? []);
                setLoadingChart6(false);
            },
            () => setLoadingChart6(false),
        );
    };

    const fetchChart9 = () => {
        setLoadingChart9(true);
        getMyDashboardPMByFarm(
            { days, granularity },
            (res: any) => {
                const { chartData, facilities, avgOnTime, avgLate } = pivotPMByFarmRows(res?.data ?? []);
                setPmByFarmChartData(chartData);
                setPmFarmFacilities(facilities);
                setPmFarmAvgOnTime(avgOnTime);
                setPmFarmAvgLate(avgLate);
                setLoadingChart9(false);
            },
            () => setLoadingChart9(false),
        );
    };

    const fetchChart10 = () => {
        setLoadingChart10(true);
        getMyDashboardPMOverTime(
            { days, granularity },
            (res: any) => {
                setPmOverTime(res?.data ?? []);
                setLoadingChart10(false);
            },
            () => setLoadingChart10(false),
        );
    };

    const fetchChart7 = () => {
        setLoadingChart7(true);
        getMyDashboardWalkthroughsByFarm(
            { days, granularity },
            (res: any) => {
                const { chartData, facilities } = pivotWalkByFarmRows(res?.data ?? []);
                setWalkByFarmChartData(chartData);
                setWalkFarmFacilities(facilities);
                setLoadingChart7(false);
            },
            () => setLoadingChart7(false),
        );
    };

    const fetchChart8 = () => {
        setLoadingChart8(true);
        getMyDashboardWalkthroughsOverTime(
            { days, granularity },
            (res: any) => {
                setWalkOverTime(res?.data ?? []);
                setLoadingChart8(false);
            },
            () => setLoadingChart8(false),
        );
    };

    const fetchChart11 = () => {
        setLoadingChart11(true);
        getMyDashboardReactiveByFarm(
            { days, granularity },
            (res: any) => {
                const { chartData, facilities, avgTasks, avgHrs } = pivotReactiveByFarmRows(res?.data ?? []);
                setReactiveByFarmChartData(chartData);
                setReactiveFarmFacilities(facilities);
                setReactiveFarmAvgTasks(avgTasks);
                setReactiveFarmAvgHrs(avgHrs);
                setLoadingChart11(false);
            },
            () => setLoadingChart11(false),
        );
    };

    const fetchChart12 = () => {
        setLoadingChart12(true);
        getMyDashboardReactiveOverTime(
            { days, granularity },
            (res: any) => {
                setReactiveOverTime(res?.data ?? []);
                setLoadingChart12(false);
            },
            () => setLoadingChart12(false),
        );
    };

    const fetchCompleteness = () => {
        setLoadingCompleteness(true);
        getMyDashboardWRCompletenessByFarm(
            { days },
            (res: any) => { setWrCompleteness(res?.data ?? []); setLoadingCompleteness(false); },
            () => setLoadingCompleteness(false),
        );
    };

    const fetchCompletenessByMe = () => {
        setLoadingCompletenessByMe(true);
        getMyDashboardWRCompletenessByMe(
            { days },
            (res: any) => { setWrCompletenessByMe(res?.data ?? []); setLoadingCompletenessByMe(false); },
            () => setLoadingCompletenessByMe(false),
        );
    };

    useEffect(() => {
        fetchChart1();
        fetchChart2();
        fetchChart3();
        fetchChart4();
        fetchChart5();
        fetchChart6();
        fetchChart9();
        fetchChart10();
        fetchChart7();
        fetchChart8();
        fetchChart11();
        fetchChart12();
        fetchCompleteness();
        fetchCompletenessByMe();
    }, [days, granularity]);

    // ── Export helper ────────────────────────────────────────────────────────

    const doExport = (key: string, apiFn: Function, req: any, filename: string, fmt: 'pdf' | 'xls') => {
        setExportingChart(`${key}-${fmt}`);
        apiFn(
            req,
            (resp: any) => { fmt === 'pdf' ? downloadPDF(resp, filename) : downloadExcel(resp, filename); setExportingChart(null); },
            () => setExportingChart(null),
        );
    };

    const ExportBtn = ({ chartKey, pdfFn, xlsFn, req, filename }: {
        chartKey: string; pdfFn: Function; xlsFn: Function; req: any; filename: string;
    }) => {
        const isBusy = exportingChart?.startsWith(chartKey) ?? false;
        return (
            <Dropdown position='end'>
                <Dropdown.Button>
                    <button
                        disabled={isBusy}
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, border: '1px solid #16a34a', background: '#16a34a', cursor: isBusy ? 'not-allowed' : 'pointer', color: '#fff', opacity: isBusy ? 0.5 : 1 }}
                    >
                        {isBusy ? '…' : 'Export'}
                    </button>
                </Dropdown.Button>
                <Dropdown.Content>
                    <Menu onClick={() => doExport(chartKey, pdfFn, req, filename, 'pdf')}>PDF</Menu>
                    <Menu onClick={() => doExport(chartKey, xlsFn, req, filename, 'xls')}>XLS</Menu>
                </Dropdown.Content>
            </Dropdown>
        );
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Layout>
            <Header title='My Dashboard'>
                <Header.Right>
                    <div className='flex gap-0.5'>
                        {TIME_PERIODS.map(p => (
                            <button
                                key={p}
                                onClick={() => setTimePeriod(p)}
                                className={`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${timePeriod === p
                                    ? 'bg-[#1e3a2f] text-white'
                                    : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </Header.Right>
            </Header>

            <div className='flex-grow overflow-auto bg-[#f9fafb] p-6'>

                <p className='text-[13px] text-[#6b7280] mb-4'>
                    Showing data for: <span className='font-medium text-[#111827]'>{PERIOD_LABELS[timePeriod]}</span>
                </p>

                {/* ── Row 1 — Work Requests ── */}
                <div className='grid grid-cols-2 gap-5 mb-5'>

                    {/* Chart 1: Facility-wise Work Requests Created */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Facility-wise Work Requests Created</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>WR count over time across my facilities vs system average</p>
                            </div>
                            <ExportBtn
                                chartKey='wrByFacilityOT'
                                pdfFn={exportMyDashboardWRByFacilityOTToPDF}
                                xlsFn={exportMyDashboardWRByFacilityOTToXLS}
                                req={{ days, granularity }}
                                filename='Facility-wise WR Created'
                            />
                        </div>
                        {loadingChart1 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <LineChart data={wrFacilityChartData} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            {wrFacilities.map((f, i) => (
                                                <Line
                                                    key={f.facilityID}
                                                    type='monotone'
                                                    dataKey={`f_${f.facilityID}`}
                                                    name={f.facilityName}
                                                    stroke={FACILITY_COLORS[i % FACILITY_COLORS.length]}
                                                    strokeWidth={2}
                                                    dot={{ r: 3 }}
                                                />
                                            ))}
                                            <Line type='monotone' dataKey='sysAvg' name='System Avg' stroke='#6b7280' strokeWidth={2} strokeDasharray='5 4' dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    {wrFacilities.map((f, i) => (
                                        <span key={f.facilityID} className='flex items-center gap-1.5'>
                                            <LineSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} />
                                            {f.facilityName}
                                        </span>
                                    ))}
                                    <span className='flex items-center gap-1.5'>
                                        <LineSwatch color='#6b7280' dashed /> System avg (all facilities)
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 2: Work Requests Created by Me */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Work Requests Created by Me</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>My total WRs over time vs {wrOverTime.length > 0 ? `${(wrOverTime[0].roleName?.toLowerCase() ?? 'peer')} average` : 'peer average'}</p>
                            </div>
                            <ExportBtn
                                chartKey='wrOverTime'
                                pdfFn={exportMyDashboardWROverTimeToPDF}
                                xlsFn={exportMyDashboardWROverTimeToXLS}
                                req={{ days, granularity }}
                                filename='Work Requests Created by Me'
                            />
                        </div>
                        {loadingChart2 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={wrOverTime} margin={{ top: 24, right: 16, left: 0, bottom: 16 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='bucketLabel' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            <Bar dataKey='userWRCount' name='My WRs' fill='#1e3a2f' radius={[3, 3, 0, 0]} maxBarSize={36} />
                                            {wrOverTime.length > 0 && wrOverTime[0].peerAvg != null && (
                                                <ReferenceLine
                                                    y={wrOverTime[0].peerAvg ?? 0}
                                                    stroke='#dc2626'
                                                    strokeDasharray='5 4'
                                                    strokeWidth={2}
                                                    label={{ value: `${wrOverTime[0].roleName ?? 'Peer'} avg (${wrOverTime[0].peerAvg ?? 0})`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#1e3a2f' /> My WRs</span>
                                    {wrOverTime.length > 0 && wrOverTime[0].peerAvg != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> {wrOverTime[0].roleName ?? 'Peer'} avg ({wrOverTime[0].peerAvg ?? 0})</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>
                {/* WR Completeness by Farm */}
                <div className='mt-6 bg-white rounded-lg p-5' style={CARD_SHADOW}>
                    <div className='flex items-start justify-between mb-4'>
                        <div>
                            <p className='text-[14px] font-semibold text-[#111827]'>Work Request Completeness by Farm</p>
                            <p className='text-[12px] text-[#6b7280]'>% of WRs missing key fields per farm — driven by global date filter</p>
                        </div>
                        <ExportBtn
                            chartKey='wrCompleteness'
                            pdfFn={exportMyDashboardWRCompletenessByFarmToPDF}
                            xlsFn={exportMyDashboardWRCompletenessByFarmToXLS}
                            req={{ days }}
                            filename='WR Completeness By Farm'
                        />
                    </div>
                    {loadingCompleteness ? <LoadingState /> : (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-xs border-collapse'>
                                <thead>
                                    <tr className='border-b border-[#e5e7eb] bg-[#f9fafb]'>
                                        <th className='py-2 px-3 text-left font-semibold text-[#374151]'>Farm</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>Total WRs</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Asset %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Zone %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Note %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Sub-Facility %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Picture %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Line %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wrCompleteness.map((row: any, i: number) => {
                                        const isTotal = row.rowType === 'Total';
                                        const isOther = row.rowType === 'OtherAvg';
                                        const pct = (v: number) => {
                                            if (v === 0) return <span className='text-[#6b7280]'>0%</span>;
                                            if (isOther) return <span className='text-[#6b7280]'>{v}%</span>;
                                            return <span className={v >= 80 ? 'text-red-600 font-semibold' : v >= 50 ? 'text-orange-500 font-medium' : 'text-[#374151]'}>{v}%</span>;
                                        };
                                        if (isTotal) return (
                                            <tr key={i} className='border-t-2 border-[#d1d5db] bg-[#f9fafb]'>
                                                <td className='py-1.5 px-3 font-bold text-[#111827]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right font-bold text-[#111827]'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                        if (isOther) return (
                                            <tr key={i} className='border-t border-dashed border-[#d1d5db]'>
                                                <td className='py-1.5 px-3 italic text-[#6b7280]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right italic text-[#6b7280]'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                        return (
                                            <tr key={i} className='border-b border-[#f3f4f6] hover:bg-[#f9fafb]'>
                                                <td className='py-1.5 px-3 font-medium text-[#111827]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right text-[#374151] font-medium'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* WR Completeness by Me */}
                <div className='mt-6 bg-white rounded-lg p-5' style={CARD_SHADOW}>
                    <div className='flex items-start justify-between mb-4'>
                        <div>
                            <p className='text-[14px] font-semibold text-[#111827]'>Work Request Completeness by Me</p>
                            <p className='text-[12px] text-[#6b7280]'>% of my WRs missing key fields per farm — driven by global date filter</p>
                        </div>
                        <ExportBtn
                            chartKey='wrCompletenessByMe'
                            pdfFn={exportMyDashboardWRCompletenessByMeToPDF}
                            xlsFn={exportMyDashboardWRCompletenessByMeToXLS}
                            req={{ days }}
                            filename='WR Completeness By Me'
                        />
                    </div>
                    {loadingCompletenessByMe ? <LoadingState /> : (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-xs border-collapse'>
                                <thead>
                                    <tr className='border-b border-[#e5e7eb] bg-[#f9fafb]'>
                                        <th className='py-2 px-3 text-left font-semibold text-[#374151]'>Farm</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>Total WRs</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Asset %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Zone %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Note %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Sub-Facility %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Picture %</th>
                                        <th className='py-2 px-3 text-right font-semibold text-[#374151]'>No Line %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wrCompletenessByMe.map((row: any, i: number) => {
                                        const isTotal = row.rowType === 'Total';
                                        const isOther = row.rowType === 'OtherAvg';
                                        const pct = (v: number) => {
                                            if (v === 0) return <span className='text-[#6b7280]'>0%</span>;
                                            if (isOther) return <span className='text-[#6b7280]'>{v}%</span>;
                                            return <span className={v >= 80 ? 'text-red-600 font-semibold' : v >= 50 ? 'text-orange-500 font-medium' : 'text-[#374151]'}>{v}%</span>;
                                        };
                                        if (isTotal) return (
                                            <tr key={i} className='border-t-2 border-[#d1d5db] bg-[#f9fafb]'>
                                                <td className='py-1.5 px-3 font-bold text-[#111827]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right font-bold text-[#111827]'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right font-bold'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                        if (isOther) return (
                                            <tr key={i} className='border-t border-dashed border-[#d1d5db]'>
                                                <td className='py-1.5 px-3 italic text-[#6b7280]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right italic text-[#6b7280]'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                        return (
                                            <tr key={i} className='border-b border-[#f3f4f6] hover:bg-[#f9fafb]'>
                                                <td className='py-1.5 px-3 font-medium text-[#111827]'>{row.facilityName}</td>
                                                <td className='py-1.5 px-3 text-right text-[#374151] font-medium'>{row.totalWRs}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoAsset)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoZone)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoNote)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoSubFacility)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoPicture)}</td>
                                                <td className='py-1.5 px-3 text-right'>{pct(row.pctNoLine)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* ── Row 3 — Tasks Completed ── */}
                <div className='grid grid-cols-2 gap-5 mb-5 mt-6'>

                    {/* Chart 3: Facility-wise Tasks Completed */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Facility-wise Tasks Completed</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>Closed-Complete + Closed-Incomplete tasks over time across my facilities vs system average</p>
                            </div>
                            <ExportBtn
                                chartKey='tasksByFacilityOT'
                                pdfFn={exportMyDashboardTasksByFacilityOTToPDF}
                                xlsFn={exportMyDashboardTasksByFacilityOTToXLS}
                                req={{ days, granularity }}
                                filename='Facility-wise Tasks Completed'
                            />
                        </div>
                        {loadingChart3 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <LineChart data={taskFacilityChartData} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            {taskFacilities.map((f, i) => (
                                                <Line
                                                    key={f.facilityID}
                                                    type='monotone'
                                                    dataKey={`f_${f.facilityID}`}
                                                    name={f.facilityName}
                                                    stroke={FACILITY_COLORS[i % FACILITY_COLORS.length]}
                                                    strokeWidth={2}
                                                    dot={{ r: 3 }}
                                                />
                                            ))}
                                            <Line type='monotone' dataKey='sysAvg' name='System Avg' stroke='#6b7280' strokeWidth={2} strokeDasharray='5 4' dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    {taskFacilities.map((f, i) => (
                                        <span key={f.facilityID} className='flex items-center gap-1.5'>
                                            <LineSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} />
                                            {f.facilityName}
                                        </span>
                                    ))}
                                    <span className='flex items-center gap-1.5'>
                                        <LineSwatch color='#6b7280' dashed /> System avg (all facilities)
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 4: Tasks Completed by Me */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Tasks Completed by Me</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>My completed tasks over time vs {taskOverTime.length > 0 ? `${(taskOverTime[0]?.roleName?.toLowerCase() ?? 'peer')} average` : 'peer average'} (Closed-Complete + Closed-Incomplete)</p>
                            </div>
                            <ExportBtn
                                chartKey='tasksOverTime'
                                pdfFn={exportMyDashboardTasksOverTimeToPDF}
                                xlsFn={exportMyDashboardTasksOverTimeToXLS}
                                req={{ days, granularity }}
                                filename='Tasks Completed by Me'
                            />
                        </div>
                        {loadingChart4 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={taskOverTime} margin={{ top: 24, right: 16, left: 0, bottom: 16 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='bucketLabel' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            <Bar dataKey='userTaskCount' name='My completed tasks' fill='#1e3a2f' radius={[3, 3, 0, 0]} maxBarSize={36} />
                                            {taskOverTime.length > 0 && taskOverTime[0].peerAvg != null && (
                                                <ReferenceLine
                                                    y={taskOverTime[0].peerAvg ?? 0}
                                                    stroke='#dc2626'
                                                    strokeDasharray='5 4'
                                                    strokeWidth={2}
                                                    label={{ value: `${taskOverTime[0].roleName ?? 'Peer'} avg (${taskOverTime[0].peerAvg ?? 0})`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#1e3a2f' /> My completed tasks</span>
                                    {taskOverTime.length > 0 && taskOverTime[0].peerAvg != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> {taskOverTime[0].roleName ?? 'Peer'} avg ({taskOverTime[0].peerAvg ?? 0})</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>

                {/* ── Row 3 — Responsiveness ── */}
                <div className='grid grid-cols-2 gap-5 mb-5'>

                    {/* Chart 5: Farm Responsiveness */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Farm Responsiveness</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>Time from task assignment to completion — by facility (driven by global date filter)</p>
                            </div>
                            <ExportBtn
                                chartKey='respByFarm'
                                pdfFn={exportMyDashboardRespByFarmToPDF}
                                xlsFn={exportMyDashboardRespByFarmToXLS}
                                req={{ days }}
                                filename='Farm Responsiveness'
                            />
                        </div>
                        {loadingChart5 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={respByFarmChartData} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} />
                                            {respFarmFacilities.map((f, i) => (
                                                <Bar
                                                    key={f.facilityID}
                                                    dataKey={`f_${f.facilityID}`}
                                                    name={f.facilityName}
                                                    fill={FACILITY_COLORS[i % FACILITY_COLORS.length]}
                                                    radius={[3, 3, 0, 0]}
                                                    maxBarSize={32}
                                                />
                                            ))}
                                            {respFarmSysAvgHours > 0 && (
                                                <ReferenceLine
                                                    x={hoursToBucket(respFarmSysAvgHours)}
                                                    stroke='#dc2626'
                                                    strokeDasharray='5 4'
                                                    strokeWidth={2}
                                                    label={{ value: `Sys avg: ${respFarmSysAvgHours} hrs`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    {respFarmFacilities.map((f, i) => (
                                        <span key={f.facilityID} className='flex items-center gap-1.5'>
                                            <SquareSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} />
                                            {f.facilityName}
                                        </span>
                                    ))}
                                    {respFarmSysAvgHours > 0 && (
                                        <span className='flex items-center gap-1.5'>
                                            <LineSwatch color='#dc2626' dashed />
                                            Sys avg: {respFarmSysAvgHours} hrs
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 6: Individual Responsiveness */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Individual Responsiveness</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>Time from task assignment to completion — vs {respIndividual.length > 0 ? `${(respIndividual[0].roleName?.toLowerCase() ?? 'peer')} average` : 'peer average'} (driven by global date filter)</p>
                            </div>
                            <ExportBtn
                                chartKey='respIndividual'
                                pdfFn={exportMyDashboardRespIndToPDF}
                                xlsFn={exportMyDashboardRespIndToXLS}
                                req={{ days }}
                                filename='My Response Time Breakdown'
                            />
                        </div>
                        {loadingChart6 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={respIndividual} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='binLabel' {...AXIS_PROPS} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} />
                                            <Bar dataKey='userCount' name='My Count' fill='#1e3a2f' radius={[3, 3, 0, 0]} maxBarSize={48} />
                                            {respIndividual.length > 0 && respIndividual[0].peerAvgHours > 0 && (
                                                <ReferenceLine
                                                    x={hoursToBucket(respIndividual[0].peerAvgHours)}
                                                    stroke='#dc2626'
                                                    strokeDasharray='5 4'
                                                    strokeWidth={2}
                                                    label={{ value: `${respIndividual[0].roleName} avg: ${respIndividual[0].peerAvgHours} hrs`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-3'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#1e3a2f' /> My Count</span>
                                    {respIndividual.length > 0 && respIndividual[0].peerAvgHours > 0 && (
                                        <span className='flex items-center gap-1.5'>
                                            <LineSwatch color='#dc2626' dashed />
                                            {respIndividual[0].roleName} avg: {respIndividual[0].peerAvgHours} hrs
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>



                {/* ── Row 4 — Walkthroughs ── */}

                <div className='grid grid-cols-2 gap-5 mb-5'>

                    {/* Chart 7: Farm Walkthroughs / Day */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Farm Walkthroughs</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>Solid = total completed · Dashed = completed with incomplete checklist · Lines meet = all checklists incomplete · Dashed = 0 means all done</p>
                            </div>
                            <ExportBtn
                                chartKey='walkByFarm'
                                pdfFn={exportMyDashboardWalkByFarmToPDF}
                                xlsFn={exportMyDashboardWalkByFarmToXLS}
                                req={{ days, granularity }}
                                filename='Farm Walkthroughs'
                            />
                        </div>
                        {loadingChart7 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <LineChart data={walkByFarmChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            {walkFarmFacilities.map((f, i) => (
                                                <React.Fragment key={f.facilityID}>
                                                    <Line
                                                        type='monotone'
                                                        dataKey={`f_${f.facilityID}_c`}
                                                        name={`${f.facilityName} (total)`}
                                                        stroke={FACILITY_COLORS[i % FACILITY_COLORS.length]}
                                                        strokeWidth={2}
                                                        dot={{ r: 3 }}
                                                    />
                                                    <Line
                                                        type='monotone'
                                                        dataKey={`f_${f.facilityID}_i`}
                                                        name={`${f.facilityName} (incomplete CL)`}
                                                        stroke={FACILITY_COLORS[i % FACILITY_COLORS.length]}
                                                        strokeWidth={2}
                                                        strokeDasharray='5 4'
                                                        dot={false}
                                                    />
                                                </React.Fragment>
                                            ))}
                                            <Line type='monotone' dataKey='sysAvg' name='System Avg' stroke='#6b7280' strokeWidth={2} strokeDasharray='3 3' dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    {walkFarmFacilities.map((f, i) => (
                                        <span key={f.facilityID} className='flex items-center gap-1.5'>
                                            <LineSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} />
                                            {f.facilityName}
                                        </span>
                                    ))}
                                    <span className='flex items-center gap-1.5'><LineSwatch color='#6b7280' dashed /> System avg</span>
                                    <span className='flex items-center gap-1.5 text-[#6b7280]'>Solid = total completed · Dashed = with incomplete checklist</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 8: My Walkthroughs / Day */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>My Walkthroughs</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>
                                    My completed walkthroughs vs incomplete checklists vs {walkOverTime.length > 0 ? `${(walkOverTime[0].roleName?.toLowerCase() ?? 'peer')} average` : 'peer average'}
                                </p>
                            </div>
                            <ExportBtn
                                chartKey='walkOverTime'
                                pdfFn={exportMyDashboardWalkOverTimeToPDF}
                                xlsFn={exportMyDashboardWalkOverTimeToXLS}
                                req={{ days, granularity }}
                                filename='My Walkthroughs'
                            />
                        </div>
                        {loadingChart8 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={walkOverTime} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='bucketLabel' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            <Bar dataKey='completedCount' name='Total completed' fill='#1e3a2f' radius={[3, 3, 0, 0]} maxBarSize={32} />
                                            <Bar dataKey='incompleteCLCount' name='Incomplete CL' fill='#d97706' radius={[3, 3, 0, 0]} maxBarSize={32} />
                                            {walkOverTime.length > 0 && walkOverTime[0].peerAvg != null && (
                                                <ReferenceLine
                                                    y={walkOverTime[0].peerAvg ?? 0}
                                                    stroke='#dc2626'
                                                    strokeDasharray='5 4'
                                                    strokeWidth={2}
                                                    label={{ value: `${walkOverTime[0].roleName ?? 'Peer'} avg (${walkOverTime[0].peerAvg ?? 0})`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#1e3a2f' /> Total completed</span>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#d97706' /> Incomplete CL</span>
                                    {walkOverTime.length > 0 && walkOverTime[0].peerAvg != null && (
                                        <span className='flex items-center gap-1.5'>
                                            <LineSwatch color='#dc2626' dashed />
                                            {walkOverTime[0].roleName ?? 'Peer'} avg ({walkOverTime[0].peerAvg ?? 0})
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>
                {/* ── Row 5 — PM Completion ── */}
                <div className='grid grid-cols-2 gap-5 mb-5'>

                    {/* Chart 9: Farm PM Completion */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Farm PM Completion</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>PM tasks completed as scheduled vs after scheduled — by facility</p>
                            </div>
                            <ExportBtn
                                chartKey='pmByFarm'
                                pdfFn={exportMyDashboardPMByFarmToPDF}
                                xlsFn={exportMyDashboardPMByFarmToXLS}
                                req={{ days, granularity }}
                                filename='Farm PM Completion'
                            />
                        </div>
                        {loadingChart9 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={pmByFarmChartData} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            {pmFarmFacilities.map((f, i) => (
                                                <React.Fragment key={f.facilityID}>
                                                    <Bar dataKey={`f_${f.facilityID}_on`} name={`On time (${f.facilityName})`} fill={FACILITY_COLORS[i % FACILITY_COLORS.length]} radius={[3, 3, 0, 0]} maxBarSize={20} />
                                                    <Bar dataKey={`f_${f.facilityID}_late`} name={`Late (${f.facilityName})`} fill={FACILITY_LATE_COLORS[i % FACILITY_LATE_COLORS.length]} radius={[3, 3, 0, 0]} maxBarSize={20} />
                                                </React.Fragment>
                                            ))}
                                            {pmFarmAvgOnTime > 0 && (
                                                <ReferenceLine y={pmFarmAvgOnTime} stroke='#16a34a' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: 'Avg on time', position: 'insideTopLeft', fill: '#16a34a', fontSize: 11 }} />
                                            )}
                                            {pmFarmAvgLate > 0 && (
                                                <ReferenceLine y={pmFarmAvgLate} stroke='#dc2626' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: 'Avg late', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }} />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-4 gap-y-2'>
                                    {pmFarmFacilities.map((f, i) => (
                                        <React.Fragment key={f.facilityID}>
                                            <span className='flex items-center gap-1.5'><SquareSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} /> On time ({f.facilityName})</span>
                                            <span className='flex items-center gap-1.5'><SquareSwatch color={FACILITY_LATE_COLORS[i % FACILITY_LATE_COLORS.length]} /> Late ({f.facilityName})</span>
                                        </React.Fragment>
                                    ))}
                                    {pmFarmAvgOnTime > 0 && <span className='flex items-center gap-1.5'><LineSwatch color='#16a34a' dashed /> Avg on time</span>}
                                    {pmFarmAvgLate > 0 && <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> Avg late</span>}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 10: My PM Completion */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>My PM Completion</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>
                                    My PM tasks completed as scheduled vs after scheduled vs {pmOverTime.length > 0 ? `${(pmOverTime[0].roleName?.toLowerCase() ?? 'peer')} averages` : 'peer averages'}
                                </p>
                            </div>
                            <ExportBtn
                                chartKey='pmOverTime'
                                pdfFn={exportMyDashboardPMOverTimeToPDF}
                                xlsFn={exportMyDashboardPMOverTimeToXLS}
                                req={{ days, granularity }}
                                filename='My PM Completion'
                            />
                        </div>
                        {loadingChart10 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={pmOverTime} margin={{ top: 24, right: 16, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='bucketLabel' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            <Bar dataKey='onTimeCount' name='On time' fill='#1e3a2f' radius={[3, 3, 0, 0]} maxBarSize={32} />
                                            <Bar dataKey='lateCount' name='Late' fill='#d97706' radius={[3, 3, 0, 0]} maxBarSize={32} />
                                            {pmOverTime.length > 0 && pmOverTime[0].peerAvgOnTime != null && (
                                                <ReferenceLine y={pmOverTime[0].peerAvgOnTime ?? 0} stroke='#16a34a' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: `${pmOverTime[0].roleName ?? 'Peer'} avg on time (${pmOverTime[0].peerAvgOnTime ?? 0})`, position: 'insideTopLeft', fill: '#16a34a', fontSize: 11 }} />
                                            )}
                                            {pmOverTime.length > 0 && pmOverTime[0].peerAvgLate != null && (
                                                <ReferenceLine y={pmOverTime[0].peerAvgLate ?? 0} stroke='#dc2626' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: `${pmOverTime[0].roleName ?? 'Peer'} avg late (${pmOverTime[0].peerAvgLate ?? 0})`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }} />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#1e3a2f' /> On time</span>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#d97706' /> Late</span>
                                    {pmOverTime.length > 0 && pmOverTime[0].peerAvgOnTime != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#16a34a' dashed /> {pmOverTime[0].roleName ?? 'Peer'} avg on time ({pmOverTime[0].peerAvgOnTime ?? 0})</span>
                                    )}
                                    {pmOverTime.length > 0 && pmOverTime[0].peerAvgLate != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> {pmOverTime[0].roleName ?? 'Peer'} avg late ({pmOverTime[0].peerAvgLate ?? 0})</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>

                {/* ── Row 6 — Reactive Maintenance ── */}
                <div className='grid grid-cols-2 gap-5 mb-5'>

                    {/* Chart 11: Farm Reactive Maintenance */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>Farm Reactive Maintenance</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>Bars = tasks completed (left) · Lines = hours logged (right) · Reactive tasks = Closed-Complete + Closed-Incomplete</p>
                            </div>
                            <ExportBtn
                                chartKey='reactiveByFarm'
                                pdfFn={exportMyDashboardReactiveByFarmToPDF}
                                xlsFn={exportMyDashboardReactiveByFarmToXLS}
                                req={{ days, granularity }}
                                filename='Farm Reactive Maintenance'
                            />
                        </div>
                        {loadingChart11 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={reactiveByFarmChartData} margin={{ top: 8, right: 40, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='label' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis yAxisId='left' {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} label={{ value: 'Tasks', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }} />
                                            <YAxis yAxisId='right' orientation='right' {...AXIS_PROPS} domain={[0, 'auto']} label={{ value: 'Hours', angle: 90, position: 'insideRight', fill: '#dc2626', fontSize: 11 }} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            {reactiveFarmFacilities.map((f, i) => (
                                                <React.Fragment key={f.facilityID}>
                                                    <Bar yAxisId='left' dataKey={`f_${f.facilityID}_t`} name={`Tasks (${f.facilityName})`} fill={FACILITY_COLORS[i % FACILITY_COLORS.length]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                                                    <Line yAxisId='right' type='monotone' dataKey={`f_${f.facilityID}_h`} name={`Hrs (${f.facilityName})`} stroke={FACILITY_COLORS[i % FACILITY_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                                                </React.Fragment>
                                            ))}
                                            {reactiveByFarmChartData.length > 0 && (
                                                <ReferenceLine yAxisId='left' y={reactiveFarmAvgTasks} stroke='#6b7280' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: 'Avg tasks', position: 'insideTopLeft', fill: '#6b7280', fontSize: 11 }} />
                                            )}
                                            {reactiveByFarmChartData.length > 0 && (
                                                <ReferenceLine yAxisId='right' y={reactiveFarmAvgHrs} stroke='#dc2626' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: 'Avg hrs', position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }} />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    {reactiveFarmFacilities.map((f, i) => (
                                        <React.Fragment key={f.facilityID}>
                                            <span className='flex items-center gap-1.5'><SquareSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} /> Tasks ({f.facilityName})</span>
                                            <span className='flex items-center gap-1.5'><LineSwatch color={FACILITY_COLORS[i % FACILITY_COLORS.length]} /> Hrs ({f.facilityName})</span>
                                        </React.Fragment>
                                    ))}
                                    <span className='flex items-center gap-1.5'><LineSwatch color='#6b7280' dashed /> Avg tasks</span>
                                    <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> Avg hrs</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Chart 12: My Reactive Maintenance */}
                    <div className='bg-white rounded-lg p-5' style={CARD_SHADOW}>
                        <div className='flex items-start justify-between mb-1'>
                            <div>
                                <p className='text-[14px] font-semibold text-[#111827]'>My Reactive Maintenance</p>
                                <p className='text-[12px] text-[#6b7280] mb-4'>
                                    Bars = my tasks completed (left) · Line = my hours logged (right) · vs {reactiveOverTime.length > 0 ? `${(reactiveOverTime[0].roleName?.toLowerCase() ?? 'peer')} averages` : 'peer averages'}
                                </p>
                            </div>
                            <ExportBtn
                                chartKey='reactiveOverTime'
                                pdfFn={exportMyDashboardReactiveOverTimeToPDF}
                                xlsFn={exportMyDashboardReactiveOverTimeToXLS}
                                req={{ days, granularity }}
                                filename='My Reactive Maintenance'
                            />
                        </div>
                        {loadingChart12 ? <LoadingState /> : (
                            <>
                                <div style={{ height: 280 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <ComposedChart data={reactiveOverTime} margin={{ top: 24, right: 40, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
                                            <XAxis dataKey='bucketLabel' {...AXIS_PROPS} tick={<BucketXTick />} height={48} />
                                            <YAxis yAxisId='left' {...AXIS_PROPS} domain={[0, 'auto']} allowDecimals={false} label={{ value: 'Tasks', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }} />
                                            <YAxis yAxisId='right' orientation='right' {...AXIS_PROPS} domain={[0, 'auto']} label={{ value: 'Hours', angle: 90, position: 'insideRight', fill: '#dc2626', fontSize: 11 }} />
                                            <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l: any) => formatBucketTick(String(l))} />
                                            <Bar yAxisId='left' dataKey='taskCount' name='My tasks' fill='#2d6a4f' radius={[3, 3, 0, 0]} maxBarSize={36} />
                                            <Line yAxisId='right' type='monotone' dataKey='hoursLogged' name='My hours' stroke='#111827' strokeWidth={2} dot={{ r: 4 }} />
                                            {reactiveOverTime.length > 0 && reactiveOverTime[0].peerAvgTasks != null && (
                                                <ReferenceLine yAxisId='left' y={reactiveOverTime[0].peerAvgTasks ?? 0} stroke='#6b7280' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: `${reactiveOverTime[0].roleName ?? 'Peer'} avg tasks (${reactiveOverTime[0].peerAvgTasks ?? 0})`, position: 'insideTopLeft', fill: '#6b7280', fontSize: 11 }} />
                                            )}
                                            {reactiveOverTime.length > 0 && reactiveOverTime[0].peerAvgHrs != null && (
                                                <ReferenceLine yAxisId='right' y={reactiveOverTime[0].peerAvgHrs ?? 0} stroke='#dc2626' strokeDasharray='5 4' strokeWidth={2}
                                                    label={{ value: `${reactiveOverTime[0].roleName ?? 'Peer'} avg hrs (${reactiveOverTime[0].peerAvgHrs ?? 0})`, position: 'insideTopRight', fill: '#dc2626', fontSize: 11 }} />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className='flex flex-wrap items-center mt-3 text-[12px] text-[#374151] gap-x-5 gap-y-2'>
                                    <span className='flex items-center gap-1.5'><SquareSwatch color='#2d6a4f' /> My tasks</span>
                                    <span className='flex items-center gap-1.5'><LineSwatch color='#111827' /> My hours</span>
                                    {reactiveOverTime.length > 0 && reactiveOverTime[0].peerAvgTasks != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#6b7280' dashed /> {reactiveOverTime[0].roleName ?? 'Peer'} avg tasks ({reactiveOverTime[0].peerAvgTasks ?? 0})</span>
                                    )}
                                    {reactiveOverTime.length > 0 && reactiveOverTime[0].peerAvgHrs != null && (
                                        <span className='flex items-center gap-1.5'><LineSwatch color='#dc2626' dashed /> {reactiveOverTime[0].roleName ?? 'Peer'} avg hrs ({reactiveOverTime[0].peerAvgHrs ?? 0})</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                </div>

            </div>




        </Layout>
    );
};

export default MyDashboardV2;
